import { createInterfaceId } from '@gitlab/needle';
import type { AIContextItem } from '@gitlab-org/ai-context';
import { type UserAction, UserActionType } from './backend';

/**
 * The presentation axis of a single Duo CLI invocation: whether there is an
 * interactive user and how the event stream is consumed. Provided by the
 * controller layer — {@link HeadlessPresentationPolicy} for `duo run`,
 * {@link InteractivePresentationPolicy} for the TUI — and consumed by the
 * backend so it never re-derives run-mode from scattered signals.
 *
 * This is the orthogonal axis to the workflow strategy's `source`: the local
 * source serves both headless (`duo run`) and interactive (TUI) presentations,
 * so presentation cannot live on the strategy. The backend owns only this
 * contract; the concrete behaviour lives with the controller that presents it.
 */
export interface PresentationPolicy {
  /**
   * Whether the system context is sent on every turn (headless `duo run`) or
   * only seeded onto the first message (interactive, which keeps it out of
   * subsequent turns).
   */
  readonly includeSystemContextEveryTurn: boolean;

  /** True when the process outlives the turn so a scheduled notification can actually fire — interactive TUI only. */
  readonly supportsScheduledWakes: boolean;

  /**
   * The invocation's initial user-supplied context items, included alongside the
   * system context on a headless run's every turn. Empty interactively, where
   * per-turn context comes from the {@link UserAction} instead.
   */
  initialUserContextItems(): AIContextItem[];

  /** Terminal notice when a fresh (non-resumed) session can no longer serve a turn. */
  sessionEndedMessage(): string;

  /** Terminal notice when a resumed session ends; the `/new` hint is interactive-only. */
  resumedSessionEndedMessage(): string;

  /**
   * The turn's goal: the prompt for a chat turn, otherwise the headless run goal
   * (interactive has no pre-set goal).
   */
  resolveGoal(action: UserAction): string;
}

export const PresentationPolicy = createInterfaceId<PresentationPolicy>('PresentationPolicy');

/** The chat-turn goal is presentation-independent, so both policies share it. */
export function promptGoal(action: UserAction): string | undefined {
  return action.type === UserActionType.SendPrompt ? action.prompt : undefined;
}
